import OpeningTimesTable from '@atoms/OpeningTimesTable/OpeningTimesTable';
import {
  BG_DEFAULT,
  PREPR_PAGE_TYPES,
  STATIC_MAP_SECTION_ID,
} from '@constants/constants';
import useTranslations from '@hooks/useTranslations';
import FacilitiesSection from '@organisms/FacilitiesSection/FacilitiesSection';
import Hero from '@organisms/Hero/Hero';
import HeroCtas from '@organisms/Hero/HeroCtas';
import HighlightedBannerSection from '@organisms/HighlightedBannerSection/HighlightedBannerSection';
import ImageSliderSection from '@organisms/ImageSliderSection/ImageSliderSection';
import MapSection from '@organisms/MapSection/MapSection';
import Sections from '@organisms/Sections/Sections';
import TextSection from '@organisms/TextSection/TextSection';
import VideoSection from '@organisms/VideoSection/VideoSection';
import { ActivityPage as Props } from '@type-declarations/page';
import { getHeroColor } from '@utils/getHeroColor';
import { findHighlightedTag } from '@utils/getHighlightedTag';
import { useMemo } from 'react';

function ActivityTemplate({ page }: { page: Props }) {
  const {
    id,
    title,
    introText,
    image,
    phone,
    mail,
    website,
    socials,
    alignment,
    ctaHero,
    tags,
    description,
    coordinates,
    address,
    location,
    category,
    featuredLocations,
    facilities,
  } = page;
  const t = useTranslations();

  const heroColor = useMemo(() => getHeroColor({ page }), [page]);
  const highlightedTag = useMemo(() => findHighlightedTag(tags), [tags]);

  const actualAddress =
    address?.title || address?.street ? address : location?.[0]?.address;
  const actualCoordinates = coordinates || location?.[0]?.coordinates;
  const showMap = actualAddress && actualCoordinates;

  return (
    <>
      <Hero
        pageId={id}
        title={title}
        text={introText}
        image={image}
        colorVariant={heroColor}
        containerSize="narrow"
        showTopBar
        alignment={alignment}
      >
        <HeroCtas
          cta={ctaHero}
          mapId={showMap && STATIC_MAP_SECTION_ID}
          website={website}
        />
      </Hero>
      {!!description && (
        <TextSection
          typename="TextSection"
          id="activity-description"
          backgroundColor={{ color: BG_DEFAULT }}
          title={t.information}
          text={description}
        />
      )}

      <FacilitiesSection
        typename="FacilitiesSection"
        id="activity-facilities-section"
        title={t.facilities}
        backgroundColor={{ color: BG_DEFAULT }}
        facilities={facilities}
      />

      {!!page.openingHours?.regularHours.length && (
        <OpeningTimesTable
          regularHours={page.openingHours.regularHours}
          specialHours={page.openingHours.specialHours}
          message={page.openingHoursMessage}
        />
      )}
      {highlightedTag && (
        <HighlightedBannerSection
          icon={highlightedTag.icon}
          bannerTitle={
            highlightedTag.bannerTitle || t.highlightedBannerSectionTitle
          }
          bannerText={
            highlightedTag.bannerText || t.highlightedBannerSectionText
          }
          theme={highlightedTag.theme}
          bannerCta={highlightedTag.bannerCta}
        />
      )}
      {page.youtubeId && (
        <VideoSection
          id="activity-video"
          typename="VideoSection"
          video={{ youtubeId: page.youtubeId }}
          backgroundColor={{ color: BG_DEFAULT }}
          fullWidth={false}
        />
      )}
      {page.extraImages && (
        <ImageSliderSection
          id="activity-images"
          typename="ImageSliderSection"
          images={page.extraImages}
          backgroundColor={{ color: BG_DEFAULT }}
        />
      )}

      {showMap && (
        <MapSection
          id={STATIC_MAP_SECTION_ID}
          typename="MapSection"
          size="smallFixed"
          title={t.contact}
          backgroundColor={{ color: BG_DEFAULT }}
          currentLocation={{
            id: `map-pin-${id}`,
            typename: location?.[0]
              ? PREPR_PAGE_TYPES.LOCATION_PAGE
              : PREPR_PAGE_TYPES.ACTIVITY_PAGE,
            title:
              location?.[0]?.address?.title || location?.[0]?.title || title,
            icon: category?.[0]?.icon || category?.[0]?.category?.[0]?.icon,
            pos: [
              Number(actualCoordinates.latitude),
              Number(actualCoordinates.longitude),
            ],
            address: address || location?.[0]?.address,
            image: image || location?.[0]?.image,
            slug: location?.[0]?.slug || page.slug,
          }}
          locations={featuredLocations}
          information={{
            address: actualAddress,
            phone,
            mail,
            website,
            socials,
          }}
        />
      )}

      <Sections sections={page.sections} />
    </>
  );
}

export default ActivityTemplate;
